import { ALL_OPTIONS_KEY } from "../../RefactoredWizard/utils/additional-options";
import { addSelectWithFields } from "../../../utils";
import { SELECT_WITH_FIELDS } from "../../../utils/wizard";
import { ENTITY_DEFAULT_VALUES } from "./constants";

export const hasOwn = (obj, key) =>
  Object.prototype.hasOwnProperty.call(obj, key);

export const getDefaultSelectedKeys = (tree) => {
  const result = new Set();
  (tree ?? []).forEach((item) => {
    result.add(item.key);
    (item.children ?? []).forEach((child) => result.add(child.key));
  });
  return result;
};

export const computeEnabledEntities = (
  flatEntities,
  defaultKeys,
  overrides,
) => {
  const result = {};
  flatEntities.forEach((entity) => {
    result[entity.key] = hasOwn(overrides, entity.key)
      ? overrides[entity.key]
      : defaultKeys.has(entity.key);
  });
  return result;
};

export const buildRequestEntities = (flatEntities, enabled, formEntities) => {
  const entities = {};
  flatEntities.forEach((entity) => {
    if (!enabled[entity.key]) return;

    if (hasOwn(ENTITY_DEFAULT_VALUES, entity.key)) {
      const value = Number(formEntities?.[entity.key]);
      if (Number.isFinite(value) && value > 0) {
        entities[entity.key] = value;
      }
    } else {
      entities[entity.key] = 0;
    }
  });
  return entities;
};

export const resolveCartId = (carts, param) => {
  if (!param || !carts) return null;
  const lower = param.toLowerCase();
  const found = Object.entries(carts).find(
    ([key, cart]) =>
      key.toLowerCase() === lower || cart?.icon?.toLowerCase() === lower,
  );
  return found?.[0] ?? null;
};

export const parseOptionsWithInsurancesPayload = (raw) => {
  if (!raw) return { insurances: [], options: [], allOptionsConfig: null };
  const payload = raw?.payload ?? raw;
  const isArray = payload && typeof payload.length === "number";
  return {
    insurances: isArray ? payload : payload?.insurances ?? [],
    options: payload?.options ?? [],
    allOptionsConfig: payload?.all_options_config ?? null,
  };
};

export const getDefaultInsuranceId = (insurances) => {
  const item =
    insurances?.find((option) => option.default) ??
    insurances?.find((option) => option.id === 0);
  return item ? item.id : null;
};

export const getInsuranceOptionLabel = (option) => {
  if (option.id === 0) return "No need";
  const word = option.number_migrations > 1 ? "remigrations" : "remigration";
  const base = `${option.number_migrations} ${word} within ${option.insurance_period} days`;
  if (option.price_percent === true) {
    return `${base} (Paid)`;
  }
  const percent = Number(option.price_percent).toFixed(2);
  return `${base} (+${percent}% to the price of Full Migration)`;
};

export const flattenEntitiesList = (list) => {
  if (!list?.length) return [];
  const result = [];
  list.forEach((item) => {
    result.push({ ...item, level: 0 });
    (item.children || []).forEach((child) => {
      result.push({ ...child, level: 1 });
    });
  });
  return result;
};

export const getSelectedEntityKeys = (enabledState) =>
  Object.entries(enabledState ?? {})
    .filter(([, isEnabled]) => isEnabled)
    .map(([key]) => key);

export const getEnabledStateFromSelectedKeys = (entitiesList, selectedKeys) =>
  (entitiesList ?? []).reduce(
    (acc, entity) => ({
      ...acc,
      [entity.key]: selectedKeys.includes(entity.key),
    }),
    {},
  );

export const getAllOptionsRequiredKeys = (allOptionsConfig) =>
  allOptionsConfig?.options?.length ? allOptionsConfig.options : null;

export const filterSelectedOptionKeysByAvailableOptions = (
  selectedOptionKeys,
  options = [],
) => {
  const availableOptionKeys = new Set(
    (options ?? [])
      .filter((option) => option?.key && !option?.inactive)
      .map((option) => option.key),
  );

  return (selectedOptionKeys ?? []).filter((key) =>
    availableOptionKeys.has(key),
  );
};

export const formatAllOptionsTitle = (name, percent) =>
  percent != null ? (name || "").replace(/%percent%/gi, String(percent)) : name;

export const isOptionAvailable = (option) =>
  option.key && option.key !== ALL_OPTIONS_KEY && !option.inactive;

export const getKeysToAddForSelectAll = (options, requiredKeys) => {
  if (!options?.length) return [];
  if (requiredKeys?.length) {
    return requiredKeys.filter((key) =>
      options.some((option) => option.key === key && !option.inactive),
    );
  }
  return options.filter(isOptionAvailable).map((option) => option.key);
};

export const getSelectedKeysAfterDeselectAll = (selectedKeys, requiredKeys) => {
  if (requiredKeys?.length) {
    return selectedKeys.filter(
      (key) => key !== ALL_OPTIONS_KEY && !requiredKeys.includes(key),
    );
  }
  return [];
};

export const getSelectedKeysAfterToggleOption = (
  selectedKeys,
  optionKey,
  checked,
) => {
  if (checked) {
    return [...selectedKeys, optionKey];
  }
  return selectedKeys.filter((key) => key !== optionKey);
};

export const getNextSelectedOptionKeys = (
  currentSelectedKeys,
  optionKey,
  checked,
  options,
  requiredKeys,
) => {
  let nextSelectedKeys;

  if (optionKey === ALL_OPTIONS_KEY) {
    if (checked) {
      const keysToAdd = getKeysToAddForSelectAll(options, requiredKeys);
      nextSelectedKeys = [
        ALL_OPTIONS_KEY,
        ...new Set([...currentSelectedKeys, ...keysToAdd]),
      ];
    } else {
      nextSelectedKeys = getSelectedKeysAfterDeselectAll(
        currentSelectedKeys,
        requiredKeys,
      );
    }
  } else {
    nextSelectedKeys = getSelectedKeysAfterToggleOption(
      currentSelectedKeys,
      optionKey,
      checked,
    );

    if (requiredKeys?.length) {
      const availableRequiredKeys = requiredKeys.filter((key) =>
        options.some((option) => option.key === key && !option.inactive),
      );

      const hasAllRequiredSelected =
        availableRequiredKeys.length > 0 &&
        availableRequiredKeys.every((key) => nextSelectedKeys.includes(key));

      if (checked) {
        if (
          hasAllRequiredSelected &&
          !nextSelectedKeys.includes(ALL_OPTIONS_KEY)
        ) {
          nextSelectedKeys = [...nextSelectedKeys, ALL_OPTIONS_KEY];
        }
      } else if (
        availableRequiredKeys.includes(optionKey) &&
        nextSelectedKeys.includes(ALL_OPTIONS_KEY)
      ) {
        nextSelectedKeys = nextSelectedKeys.filter(
          (key) => key !== ALL_OPTIONS_KEY,
        );
      }
    }
  }

  return nextSelectedKeys;
};

export const getOptionDisplayName = (option, name, allOptionsConfig) =>
  option.key === ALL_OPTIONS_KEY
    ? formatAllOptionsTitle(name, allOptionsConfig?.percent)
    : name;

const enrichEntitiesTreeWithSelectWith = (tree) =>
  tree?.map((item) => ({
    ...item,
    fields_select_with: SELECT_WITH_FIELDS[item.key] || [],
    children: item.children?.length
      ? enrichEntitiesTreeWithSelectWith(item.children)
      : item.children,
  }));

export const applyEntitySelectionWithChildren = (
  selected,
  data,
  previousSelected,
) =>
  addSelectWithFields(selected, enrichEntitiesTreeWithSelectWith(data), {
    value: previousSelected || [],
  });

export const getHiddenOptionPriceLabel = (option) => {
  if (typeof option?.dollars !== "boolean") return null;

  const isPaid =
    option.dollars ||
    option.credits ||
    option.dollars_original ||
    option.possible_dollars_with_discount;

  return isPaid ? "Paid" : "Free";
};

export const buildEstimatorQuery = ({
  source,
  target,
  entities,
  insurance,
  options,
  hidePrices,
  fingerprint,
  email,
}) => {
  const params = new URLSearchParams();

  if (source) params.set("source", source);
  if (target) params.set("target", target);
  if (fingerprint) params.set("fingerprint", fingerprint);
  if (email) params.set("email", email);

  if (entities && typeof entities === "object") {
    Object.entries(entities).forEach(([key, value]) => {
      params.set(`entities[${key}]`, String(value));
    });
  }

  if (insurance !== undefined && insurance !== null && insurance !== "") {
    params.set("insurance", String(insurance));
  }

  if (Array.isArray(options)) {
    options.forEach((opt) => params.append("options[]", opt));
  }

  if (hidePrices) {
    params.set("hide_options_prices", "1");
    params.set("hide_insurances_prices", "1");
  }

  const qs = params.toString();
  return qs ? `?${qs}` : "";
};
export const allSelectedOptionKey = (
  selectedKeys,
  options = [],
  allOptionsConfig = null,
) => {
  const requiredKeys = getAllOptionsRequiredKeys(allOptionsConfig);
  const keysForAll = getKeysToAddForSelectAll(options, requiredKeys);
  const selected = [...(selectedKeys ?? [])];

  if (!keysForAll.length) {
    return selected.filter((key) => key !== ALL_OPTIONS_KEY);
  }

  const selectedSet = new Set(selected);
  const allSelected = keysForAll.every((key) => selectedSet.has(key));

  if (allSelected) {
    if (!selectedSet.has(ALL_OPTIONS_KEY)) {
      return [...selected, ALL_OPTIONS_KEY];
    }
    return selected;
  }

  return selected.filter((key) => key !== ALL_OPTIONS_KEY);
};

export const selectOptionsAfterEntityChange = (
  prevSelectedOptionKeys,
  options = [],
  allOptionsConfig = null,
) => {
  const requiredKeys = getAllOptionsRequiredKeys(allOptionsConfig);
  const filteredSelectedKeys = filterSelectedOptionKeysByAvailableOptions(
    prevSelectedOptionKeys,
    options,
  );

  const prevSet = new Set(prevSelectedOptionKeys ?? []);
  const keysForSelectAll = getKeysToAddForSelectAll(options, requiredKeys);
  const setExpanded =
    prevSet.has(ALL_OPTIONS_KEY) &&
    keysForSelectAll.some((key) => !prevSet.has(key));

  const nextKeys = setExpanded
    ? filteredSelectedKeys.filter((key) => key !== ALL_OPTIONS_KEY)
    : filteredSelectedKeys;

  return allSelectedOptionKey(nextKeys, options, allOptionsConfig);
};

export const sanitizeNumber = (raw) => {
  if (raw === "" || raw == null) return "";
  const cleaned = String(raw).replace(/[^\d]/g, "");
  if (cleaned === "") return "";
  return Number(cleaned);
};

export const toAutoCompleteValue = (value) =>
  value === "" || value == null ? "" : String(value);

export const isUltimate = (name) => name === "ultimate";
